/**
 * Высота блока «от своего верха до низа окна».
 *
 * Нужна там, где под неподвижной шапкой раздела лежит длинный список: журнал
 * заказов, плитки врачей, плитки клиник. Без неё страница прокручивается
 * целиком, и шапка — поиск, фильтры, «Создать заказ», «Добавить врача» —
 * уезжает вверх: долистав до сто пятидесятой записи и решив завести новую,
 * приходится возвращаться наверх, а потом искать то же место заново.
 *
 * Считаем, а не пишем `calc(100vh - …)`: над списком стоят плитки итогов и
 * ряды фильтров, и сколько они занимают — зависит от ширины окна, языка
 * подписей и того, перенеслись фильтры на вторую строку или нет.
 *
 * Отступ снизу равен полю страницы (`md:p-8` в `Shell`): так
 * блок кончается ровно у нижнего края, и у окна не остаётся «лишних» точек,
 * из-за которых оно всё-таки прокручивалось бы.
 *
 * На ТЕЛЕФОНЕ высота не назначается вовсе. Владелец сначала попросил закрепить
 * шапку и там, но сам же и остановил: на 390 точках шапка модуля занимает
 * пол-экрана, и списку осталось бы места на полторы записи. Обычная прокрутка
 * страницы там удобнее.
 */

import { useEffect, useRef, useState } from 'react';

export function useFillHeight(отступСнизу?: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [высота, setВысота] = useState<number | null>(null);

  /*
   * Без списка зависимостей — намеренно.
   *
   * Верх блока меняется от чего угодно наверху раздела: развернули фильтры,
   * сменился месяц, появилась строка «Сохранить». Замер дешёвый, а одинаковое
   * число React отбрасывает сам, не перерисовывая.
   */
  useEffect(() => {
    const считать = () => {
      const узел = ref.current;
      if (!узел || !window.matchMedia('(min-width: 768px)').matches) {
        setВысота(null);
        return;
      }
      /* Поле страницы: с планшета это `md:p-8`. */
      const поле = отступСнизу ?? 32;
      /*
       * От верха блока В ДОКУМЕНТЕ, а не на экране: иначе значение менялось бы
       * от самой прокрутки, и высота дрожала бы на каждом обороте колеса.
       */
      const верх = узел.getBoundingClientRect().top + window.scrollY;
      /* Меньше двухсот точек список не имеет смысла: это полторы записи. */
      setВысота(Math.max(200, Math.round(window.innerHeight - верх - поле)));
    };

    считать();
    window.addEventListener('resize', считать);
    return () => window.removeEventListener('resize', считать);
  });

  return { ref, высота };
}
